import React, { useState } from 'react';
import { CheckCircle2, XCircle, Eye,  Star} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { COURSES } from '@/lib/data';

const PENDING = [
  { id: 'p1', title: 'React Native Masterclass', instructor: 'Kavya Sharma', category: 'Web Dev', price: 34999, submitted: '2026-03-28', status: 'pending' },
  { id: 'p2', title: 'Blockchain Fundamentals', instructor: 'Rohan Verma', category: 'Emerging Tech', price: 29999, submitted: '2026-03-30', status: 'pending' },
  { id: 'p3', title: 'AWS Solutions Architect', instructor: 'Anand Pillai', category: 'Cloud', price: 44999, submitted: '2026-04-01', status: 'under_review' },
];

export default function AdminCourseApprovals() {
  const [pending, setPending] = useState(PENDING);
  const [filter, setFilter] = useState('all');

  const approve = (id) => setPending(prev => prev.map(c => c.id === id ? { ...c, status: 'approved' } : c));
  const reject = (id) => setPending(prev => prev.map(c => c.id === id ? { ...c, status: 'rejected' } : c));

  const statusColor = { pending: 'bg-amber-100 text-amber-700', under_review: 'bg-blue-100 text-blue-700', approved: 'bg-green-100 text-green-700', rejected: 'bg-red-100 text-red-700' };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-xl font-bold">Course Approvals</h2>
          <p className="text-sm text-muted-foreground">{pending.filter(c => c.status === 'pending').length} courses pending review</p>
        </div>
        <div className="flex gap-2">
          {['all', 'pending', 'under_review', 'approved', 'rejected'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`text-xs px-3 py-1.5 rounded-full font-medium transition-all ${filter === f ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}>
              {f === 'all' ? 'All' : f.replace('_', ' ')}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        {pending.filter(c => filter === 'all' || c.status === filter).map(course => (
          <div key={course.id} className="bg-card rounded-xl border p-4 flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Eye className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold text-sm">{course.title}</p>
                <Badge className={`text-xs border-0 ${statusColor[course.status]}`}>{course.status.replace('_', ' ')}</Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">{course.instructor} · {course.category} · ₹{course.price.toLocaleString('en-IN')}</p>
              <p className="text-xs text-muted-foreground">Submitted {new Date(course.submitted).toLocaleDateString()}</p>
            </div>
            {(course.status === 'pending' || course.status === 'under_review') && (
              <div className="flex gap-2 flex-shrink-0">
                <Button size="sm" className="gap-1.5 bg-green-600 hover:bg-green-700 h-8 text-xs" onClick={() => approve(course.id)}>
                  <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                </Button>
                <Button size="sm" variant="outline" className="gap-1.5 h-8 text-xs text-destructive border-destructive/30" onClick={() => reject(course.id)}>
                  <XCircle className="w-3.5 h-3.5" /> Reject
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Existing live courses */}
      <div>
        <h3 className="font-heading font-semibold mb-3 mt-6">Live Courses</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                {['Course', 'Instructor', 'Students', 'Rating', 'Price', 'Featured', 'Action'].map(h => (
                  <th key={h} className="text-left pb-2 text-muted-foreground font-medium pr-4">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              {COURSES.map(c => (
                <tr key={c.id}>
                  <td className="py-3 pr-4 font-medium max-w-[180px] truncate">{c.title}</td>
                  <td className="py-3 pr-4 text-muted-foreground text-xs">{c.instructor.name}</td>
                  <td className="py-3 pr-4">{c.students.toLocaleString()}</td>
                  <td className="py-3 pr-4">
                    <div className="flex items-center gap-1"><Star className="w-3 h-3 fill-amber-400 text-amber-400" />{c.rating}</div>
                  </td>
                  <td className="py-3 pr-4 font-medium">₹{c.price.toLocaleString('en-IN')}</td>
                  <td className="py-3 pr-4">
                    {c.featured ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-muted-foreground" />}
                  </td>
                  <td className="py-3">
                    <Button variant="ghost" size="sm" className="text-xs h-7">Toggle Featured</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}